import React, { useState, useEffect, useRef } from "react";
import { useMediaQuery } from "@mantine/hooks";
import {
  Group,
  Text,
  SimpleGrid,
  Indicator,
  ActionIcon,
  Progress,
  Button,
  FileButton,
  Box,
} from "@mantine/core";
import type { FileRejection } from "@mantine/dropzone";
import { Dropzone } from "@mantine/dropzone";
import {
  IconCheck,
  IconFileCheck,
  IconId,
  IconX,
  IconCloudUpload,
  IconTrash,
} from "@tabler/icons-react";
import type { FileDetails } from "../../../types";
import "./FileDropzone.scss";
import jpgIcon from "../../../assets/jpg_icon.png";
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_STRING,
} from "./allowedFileTypes";
import { MAX_FILE_SIZE_MB, MAX_FILE_SIZE_BYTES } from "../../../constants";
import { usePresignedUrlStore } from "@/utils/stores/presignedUrlStore";

interface FileDropzoneProps {
  fileDetails: FileDetails[];
  organizer?: boolean;
  disabled?: boolean;
  setFile: (files: File[], yours?: boolean) => void;
  dLPreviews?: any;
  reUploadState?: {
    reUpload: boolean;
    dateCreated: string;
  };
  allowUpload?: boolean;
  onCancel?: (fileName: string, documentId?: string) => void;
  onViewDocument?: (documentId: string) => void;
  uploadProgress?: { [key: string]: number };
  compactMode?: boolean;
  bulkMode?: boolean;
  maxBulkFiles?: number;
  resetTrigger?: number; // Increment this to reset the file input
}

const FileDropzone: React.FC<FileDropzoneProps> = ({
  fileDetails,
  organizer,
  disabled,
  setFile,
  dLPreviews,
  reUploadState,
  allowUpload = true,
  onCancel,
  onViewDocument,
  uploadProgress = {},
  compactMode = false,
  bulkMode = false,
  maxBulkFiles = 50,
  resetTrigger,
}) => {
  const [uploadError, setUploadError] = useState("");
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>(
    {}
  );
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Refs for resetting file inputs
  const compactFileButtonRef = useRef<() => void>(null);
  const mobileFileButtonRef = useRef<() => void>(null);

  // Use presigned URL cache store
  const { getPresignedUrl } = usePresignedUrlStore();

  // Reset file inputs when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined) {
      compactFileButtonRef.current?.();
      mobileFileButtonRef.current?.();
    }
  }, [resetTrigger]);

  // Fetch thumbnail URLs for image files using cache store
  useEffect(() => {
    const fetchThumbnails = async () => {
      // Get list of documentIds that need thumbnail URLs
      const documentsNeedingThumbnails = fileDetails.filter(
        (file) =>
          file.documentId &&
          file.type?.startsWith("image/") &&
          !thumbnailUrls[file.documentId]
      );

      // Fetch URLs for each document (cache store handles caching)
      for (const file of documentsNeedingThumbnails) {
        if (file.documentId) {
          try {
            const { presignedUrl } = await getPresignedUrl(file.documentId);
            setThumbnailUrls((prev) => ({
              ...prev,
              [file.documentId!]: presignedUrl,
            }));
          } catch (error) {
            console.error(
              `Failed to fetch thumbnail for ${file.documentId}:`,
              error
            );
            // Silently fail - will show generic icon instead
          }
        }
      }
    };

    fetchThumbnails();
  }, [fileDetails, getPresignedUrl]); // Only depend on fileDetails, not thumbnailUrls

  const handleReject = (fileRejections: FileRejection[]) => {
    const errorMessages = fileRejections.map((rejection) => {
      const { file, errors } = rejection;
      const fileName = file.name;

      const errorMessages = errors.map((error) => {
        switch (error.code) {
          case "file-invalid-type":
            return `${fileName} has an invalid file type. Allowed types are: ${ALLOWED_FILE_TYPES_STRING}.`;
          case "too-many-files":
            return bulkMode
              ? `Too many files. Please upload up to ${maxBulkFiles} files at a time.`
              : `Too many files. Please upload 1 file at a time.`;
          case "file-too-large":
            return `${fileName} is too large. Please upload a file smaller than ${MAX_FILE_SIZE_MB}MB.`;
          default:
            return `${fileName} has been rejected due to an unknown error.`;
        }
      });

      setUploadError(errorMessages.join("\n"));
      return errorMessages.join("\n");
    });

    errorMessages.forEach((errorMessage) => {
      console.error(errorMessage);
    });
  };

  const handleDrop = async (files: File[]) => {
    setUploadError("");
    setFile(files);
  };

  const renderUploadProgressItem = (file: FileDetails) => {
    // Only show preview for actual image files (not PDFs)
    const isImageFile = file.type?.startsWith("image/");
    // Use cached presigned URL from component state (fetched via cache store)
    const thumbnailUrl = file.documentId
      ? thumbnailUrls[file.documentId]
      : undefined;
    const hasPreview = thumbnailUrl && isImageFile;
    const thumbnailSize = compactMode ? 30 : 50;

    return (
      <div
        className={`file-detail-container ${compactMode ? "compact" : ""}`}
        key={file.name}
      >
        {hasPreview ? (
          <img
            src={thumbnailUrl}
            alt="Document preview"
            width={thumbnailSize}
            height={thumbnailSize}
            style={{
              objectFit: "cover",
              borderRadius: "4px",
              cursor: file.documentId ? "pointer" : "default",
            }}
            onClick={(e) => {
              if (file.documentId && onViewDocument) {
                e.stopPropagation();
                onViewDocument(file.documentId);
              }
            }}
          />
        ) : (
          <img
            src={jpgIcon}
            alt="Document icon"
            width={thumbnailSize}
            height={thumbnailSize}
          />
        )}
        <div className="file-detail">
          <Text
            size={compactMode ? "sm" : "md"}
            lineClamp={1}
            style={{
              cursor: file.documentId ? "pointer" : "default",
              textDecoration: file.documentId ? "underline" : "none",
            }}
            onClick={(e) => {
              if (file.documentId && onViewDocument) {
                e.stopPropagation();
                onViewDocument(file.documentId);
              }
            }}
            title={file.documentId ? "Click to view document" : file.name}
          >
            {file.name}
          </Text>
          <Text size={compactMode ? "xs" : "sm"} c="dimmed">
            {(file.size / 1024).toFixed(2)}KB
          </Text>
          {[25, 100].includes(uploadProgress[file.name]) && (
            <Progress
              value={uploadProgress[file.name]}
              size="sm"
              color={uploadProgress[file.name] === 100 ? "green" : "blue"}
              style={{
                width: "100%",
                marginTop: compactMode ? "0.25rem" : "0.75rem",
              }}
            />
          )}
        </div>
        <ActionIcon
          variant="subtle"
          color="red"
          onClick={() => onCancel?.(file.name, file.documentId)}
          disabled={disabled}
          size={compactMode ? "sm" : "md"}
        >
          <IconTrash size={compactMode ? 20 : 30} />
        </ActionIcon>
      </div>
    );
  };

  if (compactMode) {
    return (
      <Box className="compact-dropzone-container">
        {uploadError && (
          <Text size="xs" c="red" mb="xs">
            {uploadError}
          </Text>
        )}

        {fileDetails.length > 0 && (
          <div className="compact-file-details">
            {fileDetails.map(renderUploadProgressItem)}
          </div>
        )}

        {allowUpload && (
          <FileButton
            resetRef={compactFileButtonRef}
            onChange={(files) => {
              if (bulkMode) {
                // Bulk mode: accept multiple files
                const fileArray = Array.isArray(files)
                  ? files
                  : files
                    ? [files]
                    : [];
                if (fileArray.length > 0) setFile(fileArray);
              } else {
                // Single mode: accept one file
                const file = Array.isArray(files) ? files[0] : files;
                if (file) setFile([file]);
              }
            }}
            accept={ALLOWED_FILE_TYPES_STRING}
            multiple={bulkMode}
          >
            {(props) => (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconCloudUpload size={14} />}
                {...props}
                disabled={disabled}
                fullWidth
                mt={fileDetails.length > 0 ? "sm" : 0}
              >
                Upload
              </Button>
            )}
          </FileButton>
        )}
      </Box>
    );
  }

  return (
    <div className="dropzone-container">
      {!dLPreviews && (
        <div className="file-details-container">
          {uploadError && <Text style={{ color: "red" }}>{uploadError}</Text>}
          {fileDetails.map(renderUploadProgressItem)}
        </div>
      )}

      {allowUpload &&
        (isMobile ? (
          <FileButton
            resetRef={mobileFileButtonRef}
            onChange={(files) => {
              if (bulkMode) {
                // Bulk mode: accept multiple files
                const fileArray = Array.isArray(files)
                  ? files
                  : files
                    ? [files]
                    : [];
                if (fileArray.length > 0) setFile(fileArray);
              } else {
                // Single mode: accept one file
                const file = Array.isArray(files) ? files[0] : files;
                if (file) setFile([file]);
              }
            }}
            accept={ALLOWED_FILE_TYPES_STRING}
            multiple={bulkMode}
          >
            {(props) => (
              <Button
                radius="lg"
                color="black"
                leftSection={<IconCloudUpload />}
                size="xl"
                mt={"md"}
                variant="outline"
                {...props}
                disabled={disabled}
                style={{ width: "100%" }}
              >
                Upload document{bulkMode ? "s" : ""}
              </Button>
            )}
          </FileButton>
        ) : (
          <Dropzone
            multiple={bulkMode}
            accept={ALLOWED_FILE_TYPES}
            onDrop={handleDrop}
            disabled={disabled ?? (!dLPreviews && fileDetails.length > 0)}
            onReject={handleReject}
            maxSize={MAX_FILE_SIZE_BYTES}
            maxFiles={bulkMode ? maxBulkFiles : 1}
            className="dropzone"
            style={{
              cursor: disabled ? "not-allowed" : "pointer",
              border: "1.5px dashed #a3a9b0",
              backgroundColor: "#F3F4F8",
              borderRadius: "0.5rem",
              padding: "20px",
              textAlign: "center",
              width: "100%",
              height: dLPreviews ? "inherit" : "200px",
              overflow: "hidden",
              margin: dLPreviews ? "0" : "10px 0",
            }}
          >
            <Group
              justify="center"
              style={{
                pointerEvents: "none",
                height: "100%",
                marginTop: dLPreviews ? "0" : "3%",
              }}
            >
              {!dLPreviews ? (
                <>
                  <Dropzone.Accept>
                    <IconCheck size="3.5rem" />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconX size="3.5rem" />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <div>
                      {fileDetails.length > 0 && !organizer ? (
                        <>
                          <IconFileCheck size="3.5rem" color="green" />
                          <Text size="xl" c="dimmed">
                            {" "}
                            File Added Successfully
                          </Text>
                        </>
                      ) : (
                        <>
                          <IconCloudUpload color="#A3A9B0" size="3.5rem" />
                          <Text c="#A3A9B0" size="xl">
                            Drag or upload file{bulkMode ? "s" : ""}
                          </Text>
                          <Text
                            style={{
                              fontWeight: "500",
                            }}
                            size="sm"
                            c="#A3A9B0"
                            mt={7}
                          >
                            Accepted File Format: PDF, JPG, PNG, GIF, HEIF, HEIC
                          </Text>
                          <Text
                            style={{
                              fontWeight: "500",
                            }}
                            size="sm"
                            c="#A3A9B0"
                            mb={"1rem"}
                          >
                            {bulkMode
                              ? `Upload up to ${maxBulkFiles} files at once. Each file should not exceed ${MAX_FILE_SIZE_MB}MB`
                              : `File should not exceed ${MAX_FILE_SIZE_MB}MB`}
                          </Text>
                        </>
                      )}
                    </div>
                  </Dropzone.Idle>
                </>
              ) : (
                <Text size="sm" c="#4D5357">
                  {reUploadState?.reUpload
                    ? "View Drivers License in Documents Page. Click to Re-upload"
                    : "Drag here or click to upload Drivers License"}
                </Text>
              )}
            </Group>
          </Dropzone>
        ))}

      {dLPreviews && (
        <>
          {reUploadState?.reUpload && dLPreviews.length === 0 && (
            <div className="uploaded-text">
              <IconId />
              <p>Drivers License Uploaded {reUploadState?.dateCreated}</p>
            </div>
          )}
          <SimpleGrid
            cols={{ base: 1, sm: 4 }}
            mt={dLPreviews.length > 0 ? "xl" : 0}
          >
            {dLPreviews.length > 0 && (
              <Indicator radius="xl" label="✓" color="#1D872E" size={20}>
                {dLPreviews}
              </Indicator>
            )}
          </SimpleGrid>
        </>
      )}
    </div>
  );
};

export default FileDropzone;
